import { useEffect, useState } from 'react';
import BasicCard from '../../../../components/basicCard/BasicCard';
import dayjs from 'dayjs';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Circle, InfoCircle } from 'react-bootstrap-icons';

import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekday from 'dayjs/plugin/weekday';
import { useTranslation } from 'react-i18next';
import { analysisService, DetailedPerformanceData } from '../../../../api/services/analysisService';
import { numberFormatter } from '../../../../utils/numberFormatter';
import { TaskStatus } from '../../timelineItem';
import Tag from '../../../../components/tag/Tag';
import ProgressBar from '../../../../components/progressBar/ProgressBar';
import { Pagination } from 'antd';

dayjs.extend(localizedFormat);
dayjs.extend(weekday);

const WeeklyPerformance = () => {
  const [performanceData, setPerformanceData] = useState<DetailedPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');

  const { t } = useTranslation('inspector');

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await analysisService.getDetailedPerformance({
          page: page,
          limit: limit,
          startDate: startOfWeek.valueOf(),
          endDate: endOfWeek.valueOf(),
        });

        setPerformanceData(data);
      } catch (err) {
        console.error('Error fetching performance data:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [page, limit]);

  if (loading) {
    return (
      <BasicCard>
        <div className="w-full flex-col p-4">
          <h4 className="text-lg font-semibold tracking-tight">{t('weeklyPerformance')}</h4>
          <div className="mt-3 flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">{t('loadingPerformanceData')}</div>
          </div>
        </div>
      </BasicCard>
    );
  }

  if (error) {
    return (
      <BasicCard>
        <div className="w-full flex-col p-4">
          <h4 className="text-lg font-semibold tracking-tight">{t('weeklyPerformance')}</h4>
          <div className="mt-3 flex items-center justify-center py-8">
            <div className="text-sm text-red-500">{error}</div>
          </div>
        </div>
      </BasicCard>
    );
  }

  if (!performanceData) {
    return (
      <BasicCard>
        <div className="w-full flex-col p-4">
          <h4 className="text-lg font-semibold tracking-tight">{t('weeklyPerformance')}</h4>
          <div className="mt-3 flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">{t('noPerformanceData')}</div>
          </div>
        </div>
      </BasicCard>
    );
  }

  const tasksAssigned = performanceData?.tasks?.assigned || 0;
  const tasksCompleted = performanceData?.tasks?.completed || 0;
  const tasksCompletionRate = performanceData?.tasks?.completionRate || 0;
  const taskList = performanceData?.tasks?.taskList || [];

  const productsAssigned = performanceData?.products?.assigned || 0;
  const productsMonitored = performanceData?.products?.monitored || 0;
  const productsMonitoringRate = performanceData?.products?.monitoringRate || 0;
  const productList = performanceData?.products?.productList || [];

  const establishmentsAssigned = performanceData?.establishments?.assigned || 0;
  const establishmentsVisited = performanceData?.establishments?.visited || 0;
  const establishmentsVisitRate = performanceData?.establishments?.visitRate || 0;
  const establishmentList = performanceData?.establishments?.establishmentList || [];

  const totalItems = Math.max(
    tasksAssigned,
    productsAssigned,
    establishmentsAssigned
  );

  return (
    <BasicCard>
      <div className="w-full flex-col p-4">
        <>
          <h4 className="text-lg font-semibold tracking-tight">{t('weeklyPerformance')}</h4>
          <h5 className="text-sm">
            {t('weeklyPerformanceDateRange', {
              startDay: startOfWeek.format('D'),
              endDateWithMonth: endOfWeek.format('D MMMM'),
            })}
          </h5>
        </>

        <div className="mt-3 space-y-6">
          {/* Tasks Section */}
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <p className="text-sm font-medium text-gray-700">{t('completedTasks')}</p>
              <p className="text-sm text-gray-600">
                {tasksCompleted} - {tasksAssigned}
              </p>
            </div>

            <ProgressBar
              percentage={
                tasksAssigned > 0
                  ? Math.min(tasksCompleted / tasksAssigned, 1) * 100
                  : 0
              }
            />

            <div className="mt-1 text-xs text-gray-500">
              <p>
                {numberFormatter(tasksCompletionRate)}% {t('completed')}
              </p>
            </div>

            {/* Task List */}
            <div className="mt-3 space-y-4">
              {taskList.map((task, index) => (
                <div
                  key={task.refId + Date.now() + index}
                  className="flex flex-wrap items-center gap-1 text-sm"
                >
                  <div className="flex-1">
                    {task.isCompleted ? (
                      <CheckCircleOutlined className="mr-2 text-green-500" />
                    ) : (
                      <Circle className="mr-2 inline" />
                    )}
                    <span className="font-medium">{task.taskName}</span>
                    {task.assigneeNames && (
                      <span className="ml-2 text-gray-500">- {task.assigneeNames}</span>
                    )}
                  </div>
                  <Tag
                    tagType={
                      task.status === TaskStatus.COMPLETED
                        ? 'success'
                        : task.status === TaskStatus.IN_PROGRESS
                          ? 'caution'
                          : task.status === TaskStatus.DELAYED
                            ? 'danger'
                            : task.status === TaskStatus.PENDING
                              ? 'warning'
                              : 'neutral'
                    }
                    filled={true}
                  >
                    {t(`${task.status}`)}
                  </Tag>
                </div>
              ))}
            </div>
          </div>

          {/* Products Section */}
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <p className="text-sm font-medium text-gray-700">{t('productsRegistered')}</p>
              <p className="text-sm text-gray-600">
                {productsMonitored} - {productsAssigned}
              </p>
            </div>

            <ProgressBar
              percentage={
                productsAssigned > 0
                  ? Math.min(
                      productsMonitored / productsAssigned,
                      1
                    ) * 100
                  : 0
              }
            />

            <div className="mt-1 text-xs text-gray-500">
              <p>
                {numberFormatter(productsMonitoringRate)}% {t('completed')}
              </p>
            </div>

            {/* Product List */}
            <div className="mt-3 space-y-4">
              {productList.map((product, index) => (
                <div
                  key={product.refId + Date.now() + index}
                  className="flex items-center gap-1 text-sm"
                >
                  <div className="flex-1">
                    {product.isMonitored ? (
                      <CheckCircleOutlined className="mr-2 text-green-500" />
                    ) : (
                      <Circle className="mr-2 inline" />
                    )}
                    <span className="font-medium">{product.name}</span>
                    {product.taskName && (
                      <span className="ml-2 text-gray-500">- {product.taskName}</span>
                    )}
                    {product.lastPrice && (
                      <>
                        <span className="ml-2 text-gray-500">
                          (L. {numberFormatter(product.lastPrice)})
                        </span>
                        <span>
                          <InfoCircle
                            className="mb-1 ml-4 inline text-blue-500"
                            title={t('lastRecordedPrice')}
                          />
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Establishments Section */}
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <p className="text-sm font-medium text-gray-700">{t('establishmentsVisited')}</p>
              <p className="text-sm text-gray-600">
                {establishmentsVisited} - {establishmentsAssigned}
              </p>
            </div>

            <ProgressBar
              percentage={
                establishmentsVisited > 0 && establishmentsAssigned > 0
                  ? Math.min(
                      establishmentsVisited /
                        establishmentsAssigned,
                      1
                    ) * 100
                  : 0
              }
            />
            <div className="mt-1 text-xs text-gray-500">
              <p>
                {numberFormatter(establishmentsVisitRate)}% {t('completed')}
              </p>
            </div>

            {/* Establishment List */}
            <div className="mt-3 space-y-4">
              {establishmentList.map((establishment, index) => (
                <div key={establishment.id + index} className="flex items-center gap-1 text-sm">
                  <div className="flex-1">
                    {establishment.isVisited ? (
                      <CheckCircleOutlined className="mr-2 text-green-500" />
                    ) : (
                      <Circle className="mr-2 inline" />
                    )}
                    <span className="font-medium">{establishment.name}</span>
                    {establishment.locationName && (
                      <span className="ml-2 text-gray-500">- {establishment.locationName}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end">
          <Pagination
            size="default"
            current={page}
            pageSize={limit}
            total={totalItems}
            showSizeChanger
            pageSizeOptions={[5, 10, 20, 50]}
            onChange={(nextPage, nextPageSize) => {
              if (nextPageSize !== limit) {
                setLimit(nextPageSize);
                setPage(1);
              } else {
                setPage(nextPage);
              }
            }}
          />
        </div>
      </div>
    </BasicCard>
  );
};

export default WeeklyPerformance;
